import React, { useCallback, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Avatar,
  Box,
  Button,
  DataTable,
  DataTableCellVariants,
  DropdownMenu,
  DropdownMenuItem,
  EmptyState,
  Highlight,
  Icon,
  Illustration,
  Pagination,
  RowsSkeletonLoader,
  toast,
  useSidePanel,
  type DataTableHeaderProps,
  type DataTableRowData,
} from '@node-space/ui-components'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useDeletePaymentLimitMutation } from 'hooks/mutations/useDeletePaymentLimitMutation'
import { useGetPaymentLimits } from 'hooks/queries/useGetPaymentLimits'
import { useSearchablePagination } from 'hooks/useSearchablePagination'
import { PaymentLimitSheet } from 'pages/PaymentLimits/components/PaymentLimitSheet'
import { formatPaymentMethodLabel, LIMIT_TYPE_LABELS } from 'pages/PaymentLimits/utils'
import { reactQueryName } from 'reactQueryKeys/reactQueryKeys'
import { LimitLevel, PaymentLimitSheetType, type Limit } from 'types/paymentLimits'
import { roundNumberWithCommas } from 'utils/utils'
import { CustomerPaymentLimitSidePanel } from './CustomerPaymentLimitSidePanel'

interface CustomerPaymentLimitsTableProps {
  customerReference: string
}

export const CustomerPaymentLimitsTable = ({
  customerReference,
}: CustomerPaymentLimitsTableProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { currentAccount } = useAccountsContext()
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    useSearchablePagination(MIN_PAGINATION_SIZE)

  const [isAddSheetOpen, setIsAddSheetOpen] = useState(false)
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false)
  const [selectedLimit, setSelectedLimit] = useState<Limit | null>(null)

  const { mutate: deleteLimit, isPending: isDeleting } = useDeletePaymentLimitMutation()

  const { data: limitsData, isFetching } = useGetPaymentLimits({
    accountId: currentAccount?.reference,
    customerId: customerReference,
    pageNumber: page,
    pageSize: rowsPerPage,
    level: LimitLevel.CUSTOMER,
  })

  const limits = limitsData?.content ?? []
  const totalElements = limitsData?.totalElements ?? 0

  const hasLimits = limits?.length > 0

  const {
    isSidePanelOpen,
    sidePanelData: selectedSidePanelLimit,
    paging: sidePanelPaging,
    setIsSidePanelOpen,
    updateSidePanel,
  } = useSidePanel(limits)

  const openSidePanel = (index: number) => {
    updateSidePanel(index)
    setIsSidePanelOpen(true)
  }

  const handleEditLimit = useCallback((limit: Limit) => {
    setSelectedLimit(limit)
    setIsEditSheetOpen(true)
  }, [])

  const handleCloseSheet = useCallback(() => {
    setIsEditSheetOpen(false)
    setSelectedLimit(null)
  }, [])

  const handleViewDetails = useCallback(
    (limit: Limit, index: number) => {
      updateSidePanel(index)
      setIsSidePanelOpen(true)
    },
    [updateSidePanel, setIsSidePanelOpen]
  )

  const handleDeleteLimit = useCallback(
    (limit: Limit) => {
      deleteLimit(limit.id, {
        onSuccess: () => {
          toast({ header: t('paymentLimits.limitDeletedSuccess'), variant: 'success' })
          queryClient.invalidateQueries({
            queryKey: [reactQueryName.PAYMENT_LIMITS],
          })
        },
        onError: () => {
          toast({
            header: t('paymentLimits.limitDeleteError'),
            variant: 'error',
          })
        },
      })
    },
    [deleteLimit, t, queryClient]
  )

  const columns: DataTableHeaderProps[] = useMemo(
    () => [
      {
        key: 'paymentMethod',
        label: t('customers.paymentLimitsTable.paymentMethod'),
        variant: DataTableCellVariants.TEXT,
      },
      {
        key: 'limitType',
        label: t('customers.paymentLimitsTable.limitType'),
        variant: DataTableCellVariants.HIGHLIGHT,
      },
      {
        key: 'amount',
        label: t('customers.paymentLimitsTable.amount'),
        variant: DataTableCellVariants.TEXT,
        className: 'text-right',
      },
      {
        key: 'currency',
        label: t('customers.paymentLimitsTable.currency'),
        variant: DataTableCellVariants.TEXT,
      },
      {
        key: 'actions',
        label: '',
        variant: DataTableCellVariants.ACTION,
      },
    ],
    [t]
  )

  const tableData: DataTableRowData[] = useMemo(() => {
    if (!limits?.length) return []

    return limits.map((limit, index) => ({
      id: limit?.id ?? `limit-${index}`,
      testid: limit?.id,
      onRowClick: () => openSidePanel(index),
      cells: [
        {
          key: 'paymentMethod',
          variant: DataTableCellVariants.TEXT,
          Text: formatPaymentMethodLabel(limit.method ?? limit.qualifiers?.method ?? ''),
        },
        {
          key: 'limitType',
          variant: DataTableCellVariants.HIGHLIGHT,
          Highlights: [
            <Highlight
              key={`${limit.id}-limitType`}
              type={limit.limitType === 'DAILY' ? 'feature' : 'neutral'}
              variant="emphasis"
            >
              {t(LIMIT_TYPE_LABELS[limit.limitType] ?? limit.limitType)}
            </Highlight>,
          ],
        },
        {
          key: 'amount',
          variant: DataTableCellVariants.TEXT,
          Text: roundNumberWithCommas(limit.amount, 2),
          className: 'text-right',
        },
        {
          key: 'currency',
          variant: DataTableCellVariants.TEXT,
          Text: (
            <Box flex align="center" gap={8}>
              <Avatar name={limit.currency} type="currency" size="small" />
              <Box className="text-body-regular text-foreground-prominent">{limit.currency}</Box>
            </Box>
          ),
        },
        {
          key: 'actions',
          variant: DataTableCellVariants.ACTION,
          Actions: [
            <Box key={`menu-${limit.id}`} testId={`limit-menu-${limit.id}`}>
              <DropdownMenu
                DropdownMenuItems={[
                  <DropdownMenuItem
                    key="view"
                    LeadingIcon={<Icon name="Eye" />}
                    onClick={() => handleViewDetails(limit, index)}
                  >
                    {t('paymentLimits.viewDetails')}
                  </DropdownMenuItem>,
                  <DropdownMenuItem
                    key="edit"
                    LeadingIcon={<Icon name="SimplePencilCustom" />}
                    onClick={() => handleEditLimit(limit)}
                  >
                    {t('paymentLimits.editLimit')}
                  </DropdownMenuItem>,
                  <DropdownMenuItem
                    key="delete"
                    LeadingIcon={<Icon name="Trash" />}
                    disabled={isDeleting}
                    onClick={() => handleDeleteLimit(limit)}
                  >
                    {t('paymentLimits.removeLimit')}
                  </DropdownMenuItem>,
                ]}
              >
                <Button
                  variant="ghost"
                  noPadding
                  iconElement={<Icon name="DotsThreeVerticalCustom" />}
                />
              </DropdownMenu>
            </Box>,
          ],
        },
      ],
    }))
  }, [limits, t, handleEditLimit, handleDeleteLimit, handleViewDetails])

  if (isFetching) {
    return <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />
  }

  if (!hasLimits) {
    return (
      <>
        <EmptyState
          testId="customer-payment-limits-empty-state"
          LottiePlayer={<Illustration name="Welcome" />}
          title={t('customers.paymentLimitsTable.emptyStateTitle')}
          description={t('customers.paymentLimitsTable.emptyStateDescription')}
          PrimaryButton={
            <Button
              testId="add-customer-limit-button"
              variant="secondary"
              iconElement={<Icon name="Plus" />}
              onClick={() => setIsAddSheetOpen(true)}
            >
              {t('paymentLimits.addLimit')}
            </Button>
          }
        />

        <PaymentLimitSheet
          isOpen={isAddSheetOpen}
          type={PaymentLimitSheetType.ADD}
          customerId={customerReference}
          onClose={() => setIsAddSheetOpen(false)}
        />
      </>
    )
  }

  return (
    <>
      <Box flex justify="end" paddingB={16}>
        <Button
          testId="add-customer-limit-button"
          variant="secondary"
          iconElement={<Icon name="Plus" />}
          onClick={() => setIsAddSheetOpen(true)}
        >
          {t('paymentLimits.addLimit')}
        </Button>
      </Box>

      <DataTable name="CustomerPaymentLimitsTable" headers={columns} data={tableData} />

      {totalElements > rowsPerPage && (
        <Box paddingT={16}>
          <Pagination
            testId="customer-payment-limits-pagination"
            page={page}
            rowsPerPage={rowsPerPage}
            total={totalElements}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </Box>
      )}

      <PaymentLimitSheet
        isOpen={isAddSheetOpen}
        type={PaymentLimitSheetType.ADD}
        customerId={customerReference}
        onClose={() => setIsAddSheetOpen(false)}
      />

      <PaymentLimitSheet
        isOpen={isEditSheetOpen}
        type={PaymentLimitSheetType.EDIT}
        limit={selectedLimit}
        customerId={customerReference}
        onClose={handleCloseSheet}
      />

      <CustomerPaymentLimitSidePanel
        isOpen={isSidePanelOpen}
        limit={selectedSidePanelLimit ?? null}
        paging={sidePanelPaging}
        onClose={() => setIsSidePanelOpen(false)}
      />
    </>
  )
}
